import { Button, Tag, Typography } from "@suraasa/placebo-ui"
import { useQuery } from "@tanstack/react-query"
import api from "api"
import { queries } from "api/queries"
import { Masterclass } from "api/resources/masterclasses/types"
import MasterClassPlaceholder from "assets/Placeholder/default-masterclass-thumbnail.svg"
import { format } from "date-fns"
import { MASTERCLASS_ONBOARDING_TARGETS } from "features/Home/SuraasaPlusNewYearOnboarding/MasterclassOnboarding/types"
import { useGlobalContext } from "global/Context/context"
import { Calendar } from "iconoir-react"
import { useEffect, useState } from "react"
import { pluralize } from "utils/helpers"
import { PropsWithClassName } from "utils/types"

import RegisterButton from "../RegisterButton"
import { getUpcomingMasterclassData } from "./helpers"

const UpcomingMasterclass = ({
  renderWithTitle = false,
  isOnboardingActive = false,
  className,
}: PropsWithClassName<{
  renderWithTitle?: boolean
  isOnboardingActive?: boolean
}>) => {
  const { data, isLoading, refetch } = useQuery({
    queryKey: queries.masterclasses.upcomingMasterclass().queryKey,
    queryFn: () => api.masterclasses.overview(),
  })

  // Provided Mock Data:Mandatory for Masterclass Onboarding
  const upcomingMasterclass = isOnboardingActive
    ? getUpcomingMasterclassData({
        masterclass: data?.upcomingMasterclass,
      })
    : data?.upcomingMasterclass

  if (isLoading) {
    return (
      <div className="mb-4">
        {renderWithTitle ? (
          <>
            <Typography className="mb-2.5 !font-semibold" variant="subtitle1">
              Upcoming <span className="text-onSurface-400">Masterclasses</span>
            </Typography>
            <div className="md:w-[65%]">
              <SkeletonCard />
            </div>
          </>
        ) : (
          <SkeletonCard />
        )}
      </div>
    )
  }

  if (!upcomingMasterclass) return null

  return (
    <div className={className}>
      <div className="mb-4">
        {renderWithTitle ? (
          <>
            <Typography className="mb-2.5 !font-semibold" variant="subtitle1">
              Upcoming <span className="text-onSurface-400">Masterclasses</span>
            </Typography>
            <div className="md:w-[65%]">
              <Card data={upcomingMasterclass} refetchMasterclass={refetch} />
            </div>
          </>
        ) : (
          <Card data={upcomingMasterclass} refetchMasterclass={refetch} />
        )}
      </div>
    </div>
  )
}

const SkeletonCard = () => (
  <div className="flex animate-pulse flex-col rounded-2xl border border-surface-200 bg-surface-50 p-2.5 md:flex-row md:gap-4">
    <div className="w-full md:order-2 md:w-1/3 md:shrink-0">
      <div className="mb-4 h-[150px] rounded-2xl bg-surface-200 md:mb-0 md:h-full" />
    </div>
    <div className="flex w-full flex-col justify-between md:order-1 md:w-2/3">
      <div>
        <div className="mb-2 h-1.5 w-1/3 rounded bg-surface-200" />
        <div className="mb-2 h-3 w-2/3 rounded bg-surface-200" />
        <div className="mb-4 h-2 w-1/2 rounded bg-surface-200" />
      </div>
      <div>
        <div className="h-3 w-20 rounded-lg bg-surface-200" />
      </div>
    </div>
  </div>
)

const Card = ({
  data,
  refetchMasterclass,
}: {
  data: Masterclass
  refetchMasterclass: () => void
}) => {
  const { title, thumbnail, joiningUrl, startTime, isRegistered, slug } = data
  const [showJoinButton, setShowJoinButton] = useState(!!joiningUrl)
  const [countdown, setCountdown] = useState<number | null>(null)

  const { profile } = useGlobalContext()

  const trackMasterclass = () => {
    api.masterclasses.trackItoMasterclass({
      urlParams: { videoMeetingId: data.videoMeetingId },
      data: { joinTime: new Date().toISOString() },
    })
  }

  useEffect(() => {
    if (joiningUrl) return

    const start = new Date(startTime).getTime()
    const now = Date.now()
    const msUntilStart = start - now

    if (msUntilStart <= 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCountdown(5)
      return
    }

    const triggerCountdownTimer = setTimeout(() => {
      setCountdown(5)
    }, Math.max(msUntilStart, 0))

    return () => clearTimeout(triggerCountdownTimer)
  }, [startTime, joiningUrl])

  useEffect(() => {
    if (countdown === null) return
    if (countdown === 0) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setShowJoinButton(true)
      return
    }

    const interval = setInterval(() => {
      setCountdown(prev => (prev !== null ? prev - 1 : null))
    }, 1000)

    return () => clearInterval(interval)
  }, [countdown])

  return (
    <>
      <div
        className="flex flex-col rounded-2xl border border-surface-200 bg-[radial-gradient(93.73%_157.4%_at_60.08%_14.8%,_#FFFFFF_0%,_#F4F9FF_100%)] p-2.5 md:flex-row md:gap-4"
        data-tour={MASTERCLASS_ONBOARDING_TARGETS.upcoming}
      >
        <div className="w-full md:order-2 md:w-1/3 md:shrink-0">
          <img
            src={thumbnail || MasterClassPlaceholder}
            alt="thumbnail"
            className="mb-4 aspect-video rounded-2xl object-cover md:mb-0"
            onError={({ currentTarget }) => {
              currentTarget.onerror = null // prevents looping
              currentTarget.src = MasterClassPlaceholder
            }}
          />
        </div>

        <div className="flex w-full flex-col justify-between md:order-1 md:w-2/3">
          <div>
            <Typography variant="preTitle" className="mb-2 text-highlight-500">
              UPCOMING MASTERCLASS
            </Typography>
            <Typography
              variant="title3"
              className="first-letter-capitalize mb-1.5 text-onSurface-800"
            >
              {title}
            </Typography>
            <div className="mb-4 flex flex-row items-center gap-0.75">
              <Calendar color="#64748B" className="size-2" />
              <Typography variant="smallBody" className="text-onSurface-500">
                {format(new Date(startTime), "do MMMM yyyy, hh:mm a")}
              </Typography>
            </div>
          </div>

          <div>
            {showJoinButton && (
              <a href={joiningUrl || "#"} target="_blank" rel="noreferrer">
                <Button
                  color="black"
                  onClick={trackMasterclass}
                  className="w-full md:w-auto"
                >
                  Join Now
                </Button>
              </a>
            )}

            {!showJoinButton && countdown !== null && (
              <Tag color="success" className="mb-0.5">
                Starting in {pluralize("second", countdown)}...
              </Tag>
            )}

            {!showJoinButton && countdown === null && isRegistered && (
              <div>
                <Tag color="success" className="mb-1">
                  Spot Reserved
                </Tag>
                <Typography variant="smallBody" className="text-onSurface-500">
                  Joining link will be available at the time of the masterclass
                </Typography>
              </div>
            )}

            {!showJoinButton && countdown === null && !isRegistered && (
              <RegisterButton
                className="w-full rounded-[10px] md:w-fit"
                profile={profile}
                slug={slug}
                onSuccess={() => {
                  refetchMasterclass()
                }}
              />
            )}
          </div>
        </div>
      </div>
    </>
  )
}

export default UpcomingMasterclass
